/**
 * Бланк декларації з ПДВ (J0200126) — візуально наближений до ОФІЦІЙНОЇ
 * друкованої форми (2026-08-05, рішення власника: «як Податковий
 * розрахунок J05, щоб бухгалтер не лякався»): шрифт з зарубками, чорні
 * рамки, шапка з реквізитами (тип, період, платник, ІПН, адреса, ДПІ),
 * розділи I–III «Код | Назва | А | Б», галочки додатків, підписи.
 *
 * Використовується РЕДАКТОРОМ (поля редаговані, жовтий фон) і сторінкою
 * ДРУКУ (readOnly — значення текстом). Спільні стилі експортуються для
 * бланків додатків (VatAppendixTab).
 */
import type { CSSProperties, ReactNode } from 'react';
import type { Organization } from '@/api/catalogs';
import type { VatAppendixCode, VatDeclaration } from '@/api/vatDeclarations';
import { chiefForReports } from '@/shared/utils/orgOfficials';

// ── Спільні стилі бланка (еталон J05 FormView) ──────────────────────────────
export const SERIF = "'Times New Roman', 'PT Serif', Georgia, serif";
export const B = '1px solid #333';
export const blankBox: CSSProperties = {
  fontFamily: SERIF, color: '#111', background: '#fff' };
export const numCell: CSSProperties = { border: B, padding: '2px 5px',
  textAlign: 'center', verticalAlign: 'middle', whiteSpace: 'nowrap',
  fontWeight: 600, width: 52, fontSize: '0.78rem' };
export const nameCell: CSSProperties = { border: B, padding: '2px 6px',
  verticalAlign: 'middle', fontSize: '0.8rem', lineHeight: 1.3 };
export const valCell: CSSProperties = { border: B, padding: '1px 3px',
  verticalAlign: 'middle', width: 130 };
export const roStyle: CSSProperties = { display: 'block', padding: '2px 4px',
  fontWeight: 600, background: '#eef1f4', color: '#334155',
  minHeight: '1.1em' };
/** Глобальний `table th` (ВЕЛИКІ літери) — для бланка ДПС скидаємо. */
export const thBlank: CSSProperties = { textTransform: 'none',
  letterSpacing: 'normal', fontWeight: 400, fontSize: '0.68rem',
  color: '#111', border: B, padding: '2px 4px', textAlign: 'center',
  verticalAlign: 'middle', background: '#fff' };

export const MONTHS_UK = ['січень', 'лютий', 'березень', 'квітень', 'травень',
  'червень', 'липень', 'серпень', 'вересень', 'жовтень', 'листопад', 'грудень'];

export const APP_LABEL: Record<VatAppendixCode, string> = {
  D1: 'Д1', D2: 'Д2', D3: 'Д3', D4: 'Д4', D5: 'Д5', D6: 'Д6',
};

/** Поле бланка: редаговане (жовте, без рамки — рамка клітинки) або текст. */
export function BlankCell({ value, onChange, readOnly, align = 'right', print }: {
  value: string | number | null | undefined;
  onChange?: (raw: string) => void;
  readOnly: boolean;
  align?: 'left' | 'right' | 'center';
  print?: boolean;
}) {
  if (readOnly || !onChange) {
    return (
      <span style={{ display: 'block', padding: '2px 4px', textAlign: align,
        fontFamily: SERIF, fontSize: '0.82rem', fontWeight: 600,
        background: print ? 'transparent' : '#eef1f4', minHeight: '1.1em' }}>
        {value ?? ''}
      </span>
    );
  }
  return (
    <input type="number" step="1" value={value ?? ''}
      onChange={(e) => onChange(e.target.value)}
      style={{ width: '100%', boxSizing: 'border-box', border: 'none',
        outline: 'none', fontFamily: SERIF, fontSize: '0.82rem',
        textAlign: align, padding: '2px 4px', background: '#fefce8',
        color: '#111' }} />
  );
}

/** «X»-відмітка бланка у рамці. */
export function XMark({ on }: { on: boolean }) {
  return (
    <span style={{ display: 'inline-block', width: 26, minHeight: '1.15em',
      border: B, textAlign: 'center', fontWeight: 700, fontFamily: SERIF,
      fontSize: '0.82rem', verticalAlign: 'middle',
      background: '#fff' }}>
      {on ? 'X' : ' '}
    </span>
  );
}

const ro = (v: string | null | undefined, align: CSSProperties['textAlign'] = 'left',
            print?: boolean): ReactNode => (
  <span style={{ ...roStyle, textAlign: align,
    background: print ? 'transparent' : (roStyle.background as string) }}>
    {v || ' '}
  </span>
);

/** Шапка бланка ДОДАТКА (спільна для Д1–Д6): тип, період, платник, ІПН. */
export function AppendixHeader({ doc, org, appLabel, print }: {
  doc: VatDeclaration;
  org: Organization | undefined;
  appLabel: string;
  print?: boolean;
}) {
  const tin = org?.entity_type === 'FOP' ? (org?.inn || org?.edrpou) : org?.edrpou;
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
      marginBottom: 6 }}>
      <tbody>
        <tr>
          <td style={{ ...nameCell, width: '55%' }}>
            <b>Додаток {appLabel.replace('Д', '')}</b> до податкової декларації
            з податку на додану вартість
          </td>
          <td style={{ ...nameCell }}>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center',
              flexWrap: 'wrap' }}>
              <span>Звітна <XMark on={doc.report_type === 'REPORTING'} /></span>
              <span>Звітна нова <XMark on={doc.report_type === 'REPORTING_NEW'} /></span>
              <span>Уточнююча <XMark on={doc.report_type === 'CORRECTING'} /></span>
            </div>
          </td>
        </tr>
        <tr>
          <td style={nameCell}>
            Звітний (податковий) період: <b>{MONTHS_UK[doc.period_month - 1]}
            {' '}{doc.period_year}</b>
            {doc.report_type === 'CORRECTING' && doc.corrected_period_month && (
              <>
                {' · '}уточнюється: <b>
                  {MONTHS_UK[doc.corrected_period_month - 1]}
                  {' '}{doc.corrected_period_year}
                </b>
              </>
            )}
          </td>
          <td style={nameCell}>
            ІПН платника ПДВ: <b>{org?.vat_number || '—'}</b>
            {' · '}
            {org?.entity_type === 'FOP' ? 'РНОКПП' : 'ЄДРПОУ'}: <b>{tin || '—'}</b>
          </td>
        </tr>
        <tr>
          <td style={nameCell} colSpan={2}>
            Платник: {ro(org?.full_name || org?.name, 'left', print)}
          </td>
        </tr>
      </tbody>
    </table>
  );
}

/** Шапка ОСНОВНОЇ декларації — блоки 01–05 за офіційним бланком. */
export function DeclarationHeader({ doc, org, includedApps, print }: {
  doc: VatDeclaration;
  org: Organization | undefined;
  includedApps: VatAppendixCode[];
  print?: boolean;
}) {
  const tin = org?.entity_type === 'FOP' ? (org?.inn || org?.edrpou) : org?.edrpou;
  const sub: CSSProperties = { fontSize: '0.62rem', color: '#555',
    textAlign: 'center' };
  return (
    <div style={{ ...blankBox }}>
      <div style={{ textAlign: 'center', fontWeight: 700, fontSize: '0.95rem',
        padding: '6px 0 8px', fontFamily: SERIF }}>
        ПОДАТКОВА ДЕКЛАРАЦІЯ З ПОДАТКУ НА ДОДАНУ ВАРТІСТЬ
      </div>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <colgroup>
          <col style={{ width: 46 }} /><col /><col style={{ width: 300 }} />
        </colgroup>
        <tbody>
          {/* 01 — тип декларації */}
          <tr>
            <td style={numCell}>01</td>
            <td style={nameCell}>Тип декларації</td>
            <td style={{ ...valCell, width: 300, padding: '2px 6px' }}>
              <div style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span style={{ fontSize: '0.8rem' }}>011 Звітна{' '}
                  <XMark on={doc.report_type === 'REPORTING'} /></span>
                <span style={{ fontSize: '0.8rem' }}>012 Звітна нова{' '}
                  <XMark on={doc.report_type === 'REPORTING_NEW'} /></span>
              </div>
            </td>
          </tr>
          {/* 02 — період */}
          <tr>
            <td style={numCell}>02</td>
            <td style={nameCell}>Звітний (податковий) період</td>
            <td style={{ ...valCell, width: 300, padding: 0 }}>
              <div style={{ display: 'flex' }}>
                <div style={{ flex: 1, borderRight: B }}>
                  <div style={{ textAlign: 'center', fontWeight: 600,
                    fontSize: '0.85rem' }}>{doc.period_year}</div>
                  <div style={sub}>(рік)</div>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ textAlign: 'center', fontWeight: 600,
                    fontSize: '0.85rem' }}>
                    {String(doc.period_month).padStart(2, '0')}
                  </div>
                  <div style={sub}>(місяць)</div>
                </div>
              </div>
            </td>
          </tr>
          {/* 03 — платник */}
          <tr>
            <td style={numCell}>03</td>
            <td style={nameCell}>Платник</td>
            <td style={valCell}>{ro(org?.full_name || org?.name, 'left', print)}</td>
          </tr>
          {/* 031/032 — коди */}
          <tr>
            <td style={numCell}>031</td>
            <td style={nameCell}>
              Індивідуальний податковий номер платника ПДВ
            </td>
            <td style={valCell}>{ro(org?.vat_number, 'center', print)}</td>
          </tr>
          <tr>
            <td style={numCell}>032</td>
            <td style={nameCell}>
              {org?.entity_type === 'FOP'
                ? 'Реєстраційний номер облікової картки платника податків (РНОКПП)'
                : 'Код за ЄДРПОУ'}
            </td>
            <td style={valCell}>{ro(tin, 'center', print)}</td>
          </tr>
          {/* 04 — адреса */}
          <tr>
            <td style={numCell}>04</td>
            <td style={nameCell}>Податкова адреса</td>
            <td style={valCell}>
              {ro(org?.legal_address || org?.actual_address, 'left', print)}
            </td>
          </tr>
          {/* 05 — ДПІ */}
          <tr>
            <td style={numCell}>05</td>
            <td style={nameCell}>
              Найменування контролюючого органу, до якого подається декларація
            </td>
            <td style={valCell}>{ro(org?.sti_name, 'left', print)}</td>
          </tr>
          {/* 06 — додатки */}
          <tr>
            <td style={numCell}>06</td>
            <td style={nameCell}>
              Відмітка про подання додатків (вкладаються лише заповнені)
            </td>
            <td style={{ ...valCell, padding: '2px 6px' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {(['D1', 'D2', 'D3', 'D4', 'D5', 'D6'] as VatAppendixCode[]).map((c) => (
                  <span key={c} style={{ fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
                    {APP_LABEL[c]} <XMark on={includedApps.includes(c)} />
                  </span>
                ))}
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Підписи внизу бланка (декларація і додатки). */
export function SignaturesBlock({ doc, org, print }: {
  doc: VatDeclaration;
  org: Organization | undefined;
  print?: boolean;
}) {
  const d = doc.document_date ? new Date(doc.document_date) : null;
  const dateStr = d ? d.toLocaleDateString('uk-UA') : '';
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
      marginTop: 6 }}>
      <tbody>
        <tr>
          <td style={nameCell}>Дата подання</td>
          <td style={{ ...valCell, width: 130 }}>{ro(dateStr, 'center', print)}</td>
          <td style={nameCell}>Керівник (уповноважена особа)</td>
          <td style={{ ...valCell, width: 220 }}>
            {ro(org?.director_name, 'left', print)}
          </td>
          <td style={{ ...nameCell, width: 130 }}>РНОКПП</td>
          <td style={{ ...valCell, width: 130 }}>
            {ro(org?.director_inn ?? null, 'center', print)}
          </td>
        </tr>
        {org && chiefForReports(org) ? (   /* ч.1179: лише з галочкою на картці */
          <tr>
            <td style={nameCell} colSpan={2}></td>
            <td style={nameCell}>Головний бухгалтер</td>
            <td style={valCell}>{ro(org.chief_accountant, 'left', print)}</td>
            <td style={nameCell}>РНОКПП</td>
            <td style={valCell}>
              {ro(org?.chief_accountant_inn ?? null, 'center', print)}
            </td>
          </tr>
        ) : null}
      </tbody>
    </table>
  );
}

// ── Рядки розділів I–III (та сама розкладка, що у редакторі раніше) ─────────
export interface RowDef {
  num: string;
  label: string;
  a?: string;
  b?: string;
  bold?: boolean;
}

export const SECTION_I: RowDef[] = [
  { num: '1.1', label: 'Операції на митній території України, що оподатковуються за основною ставкою (20%)', a: 'R11GA', b: 'R11GB' },
  { num: '1.2', label: 'Операції, що оподатковуються за ставкою 7%', a: 'R12GA', b: 'R12GB' },
  { num: '1.3', label: 'Операції, що оподатковуються за ставкою 14%', a: 'R13GA', b: 'R13GB' },
  { num: '2.1', label: 'Операції з вивезення товарів за межі митної території України (0%, експорт)', a: 'R21GA' },
  { num: '2.2', label: 'Інші операції, що оподатковуються за нульовою ставкою', a: 'R22GA' },
  { num: '4.1', label: 'Нараховані податкові зобов’язання згідно з п.198.5 / ст.199 (основна ставка)', a: 'R41GA', b: 'R41GB' },
  { num: '4.2', label: 'Нараховані податкові зобов’язання згідно з п.198.5 / ст.199 (7%)', a: 'R42GA', b: 'R42GB' },
  { num: '4.3', label: 'Нараховані податкові зобов’язання згідно з п.198.5 / ст.199 (14%)', a: 'R43GA', b: 'R43GB' },
  { num: '5', label: 'Операції, що не є об’єктом оподаткування, звільнені від оподаткування, за межами митної території', a: 'R50GA' },
  { num: '5.1', label: 'з рядка 5: операції, звільнені від оподаткування', a: 'R51GA' },
  { num: '6.1', label: 'Послуги, отримані від нерезидента на митній території України (основна ставка)', a: 'R61GA', b: 'R61GB' },
  { num: '6.2', label: 'Послуги, отримані від нерезидента (7%)', a: 'R62GA', b: 'R62GB' },
  { num: '7', label: 'Коригування податкових зобов’язань', a: 'R71GA', b: 'R71GB' },
  { num: '9', label: 'Усього податкових зобов’язань', b: 'R90GB', bold: true },
];

export const SECTION_II: RowDef[] = [
  { num: '10.1', label: 'Придбання (виготовлення) з ПДВ на митній території України (основна ставка)', a: 'R101GA', b: 'R101GB' },
  { num: '10.2', label: 'Придбання з ПДВ за ставкою 7%', a: 'R102GA', b: 'R102GB' },
  { num: '10.3', label: 'Придбання з ПДВ за ставкою 14%', a: 'R103GA', b: 'R103GB' },
  { num: '10.4', label: 'Придбання без ПДВ', a: 'R104GA' },
  { num: '11.1', label: 'Ввезені на митну територію України товари (основна ставка)', a: 'R111GA', b: 'R111GB' },
  { num: '11.2', label: 'Ввезені товари за ставкою 7%', a: 'R112GA', b: 'R112GB' },
  { num: '11.3', label: 'Ввезені товари за ставкою 14%', a: 'R113GA', b: 'R113GB' },
  { num: '12', label: 'Послуги, отримані від нерезидента', a: 'R120GA', b: 'R120GB' },
  { num: '13.1', label: 'Коригування податкового кредиту', a: 'R131GA', b: 'R131GB' },
  { num: '13.2', label: 'Коригування податкового кредиту (інші)', a: 'R132GA', b: 'R132GB' },
  { num: '16', label: 'Від’ємне значення, що включається до складу податкового кредиту поточного періоду', b: 'R160GB' },
  { num: '16.1', label: 'з рядка 16: значення рядка 21 попереднього звітного періоду', b: 'R161GB' },
  { num: '16.2', label: 'з рядка 16: за уточнюючими розрахунками', b: 'R162GB' },
  { num: '16.3', label: 'з рядка 16: за результатами перевірок контролюючого органу', b: 'R163GB' },
  { num: '17', label: 'Усього податкового кредиту', b: 'R170GB', bold: true },
];

export const SECTION_III: RowDef[] = [
  { num: '18', label: 'Позитивне значення різниці між сумою податкового зобов’язання та податкового кредиту (сума ПДВ до сплати)', b: 'R180GB', bold: true },
  { num: '19', label: 'Від’ємне значення різниці між сумою податкового зобов’язання та податкового кредиту', b: 'R190GB' },
  { num: '19.1', label: 'з рядка 19: сума перевищення від’ємного значення над лімітом реєстрації', b: 'R191GB' },
  { num: '20', label: 'Від’ємне значення, що зараховується', b: 'R200GB' },
  { num: '20.1', label: 'у зменшення суми податкового боргу з ПДВ', b: 'R201GB' },
  { num: '20.2', label: 'підлягає бюджетному відшкодуванню', b: 'R202GB' },
  { num: '20.2.1', label: 'на рахунок платника у банку', b: 'R2021GB' },
  { num: '20.2.2', label: 'у рахунок сплати грошових зобов’язань з інших платежів', b: 'R2022GB' },
  { num: '20.3', label: 'зараховується до складу податкового кредиту наступного звітного періоду', b: 'R203GB' },
  { num: '21', label: 'Сума від’ємного значення, що зараховується до складу податкового кредиту наступного звітного періоду (ряд. 16.1 наступного періоду)', b: 'R210GB', bold: true },
];

export const KNOWN_CODES = new Set(
  [...SECTION_I, ...SECTION_II, ...SECTION_III]
    .flatMap((r) => [r.a, r.b]).filter(Boolean) as string[]);

/** Розділи I–III бланка. setV відсутній / readOnly → друкований вигляд. */
export function DeclarationSections({ values, setV, readOnly, print }: {
  values: Record<string, number>;
  setV?: (code: string, raw: string) => void;
  readOnly: boolean;
  print?: boolean;
}) {
  const fmt0 = (v: number | undefined) =>
    v == null ? '' : v.toLocaleString('uk-UA');
  const cell = (code?: string) => {
    if (!code) {
      return <span style={{ display: 'block', textAlign: 'center',
        color: '#999' }}>×</span>;
    }
    return (
      <BlankCell value={print ? fmt0(values[code]) : (values[code] ?? '')}
        readOnly={readOnly} print={print}
        onChange={setV ? (raw) => setV(code, raw) : undefined} />
    );
  };
  const renderSection = (title: string, rows: RowDef[]) => (
    <>
      <tr>
        <td colSpan={4} style={{ ...nameCell, fontWeight: 700,
          textAlign: 'center', background: print ? '#fff' : '#f8f8f6' }}>
          {title}
        </td>
      </tr>
      {rows.map((r) => (
        <tr key={r.num} style={{ fontWeight: r.bold ? 700 : 400 }}>
          <td style={numCell}>{r.num}</td>
          <td style={nameCell}>{r.label}</td>
          <td style={{ ...valCell }}>{cell(r.a)}</td>
          <td style={{ ...valCell }}>{cell(r.b)}</td>
        </tr>
      ))}
    </>
  );
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse', ...blankBox,
      marginTop: 6 }}>
      <colgroup>
        <col style={{ width: 52 }} /><col />
        <col style={{ width: 130 }} /><col style={{ width: 130 }} />
      </colgroup>
      <thead>
        <tr>
          <th style={thBlank}>Код рядка</th>
          <th style={thBlank}>Показник</th>
          <th style={thBlank}>Колонка А — обсяги постачання (придбання), грн</th>
          <th style={thBlank}>Колонка Б — сума ПДВ, грн</th>
        </tr>
      </thead>
      <tbody>
        {renderSection('Розділ I. ПОДАТКОВІ ЗОБОВ’ЯЗАННЯ', SECTION_I)}
        {renderSection('Розділ II. ПОДАТКОВИЙ КРЕДИТ', SECTION_II)}
        {renderSection('Розділ III. РОЗРАХУНКИ ЗА ЗВІТНИЙ ПЕРІОД', SECTION_III)}
      </tbody>
    </table>
  );
}
